/** @format */

import React, { useEffect, useState } from 'react';
import Header from 'components/Layout/Header';
import Footer from 'components/Layout/Footer';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import AlertBox from 'components/Alert';
import { AiOutlineExclamationCircle } from 'react-icons/ai';
import { useDispatch, useSelector } from 'react-redux';
import {
  loginRequestAction,
  clearLoginStateAction
} from 'stateContainer/actions';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';

const ERROR_CODES = {
  BrokerNotActive: 'BrokerNotActive',
  TemporaryPasswordExpired: 'TemporaryPasswordExpired',
  NotAuthorizedException: 'NotAuthorizedException',
  InvalidRole: 'InvalidRole'
};

export default function Login(props) {
  const text = props.t('lang_broker:login');
  const login_route = props.t('lang_route:broker_login');
  const change_password_route = props.t('lang_route:broker_change_password');
  const dashboard_route = props.t('lang_route:broker_dashboard');

  const dispatch = useDispatch();
  const sessionData = useSelector((state) => state.session);
  const [showPassword, setShowPassword] = useState(false);
  const [loginFailedMessageState, setLoginFailedMessageState] = useState(null);

  const processGeneralError = () => {
    let errorMessage = text.messages.apiErrorMessages.AL_GeneralError;
    const { errorLanguageCode } = sessionData.loginError.response.data;
    if (errorLanguageCode) {
      errorMessage = text.messages.apiErrorMessages[errorLanguageCode];
    }
    setLoginFailedMessageState(errorMessage);
  };

  const processSpecificErrors = (errorCode) => {
    let errorMessage;

    switch (errorCode) {
      case ERROR_CODES.BrokerNotActive:
        errorMessage = text.messages.apiErrorMessages.BrokerNotActive;
        break;
      case ERROR_CODES.TemporaryPasswordExpired:
        errorMessage = text.messages.apiErrorMessages.TemporaryPasswordExpired;
        break;
      case ERROR_CODES.NotAuthorizedException:
        errorMessage = text.messages.apiErrorMessages.NotAuthorizedException;
        break;
      case ERROR_CODES.InvalidRole:
        errorMessage = text.messages.apiErrorMessages.NotAuthorizedException;
        break;
      default:
        break;
    }

    setLoginFailedMessageState(errorMessage);
  };

  useEffect(() => {
    if (sessionData.loginError && sessionData.loginError.response) {
      processGeneralError();
    }

    if (Object.values(ERROR_CODES).includes(sessionData.errorCode)) {
      processSpecificErrors(sessionData.errorCode);
    }
  }, [sessionData.loginError]);

  const formikValidationSchema = () =>
    Yup.object().shape({
      email: Yup.string()
        .email(text.messages.validation.emailInvalid)
        .required(text.messages.validation.emailRequired),
      password: Yup.string().required(text.messages.validation.passwordRequired)
    });

  const formik = useFormik({
    initialValues: {
      email: '',
      password: ''
    },
    onSubmit: (values) => {
      if (values.email && values.password) {
        dispatch(clearLoginStateAction());
        dispatch(
          loginRequestAction({
            username: values.email,
            password: values.password,
            successUrl: dashboard_route,
            failedUrl: login_route,
            changePasswordUrl: change_password_route
          })
        );
      } else {
        setLoginFailedMessageState(text.messages.validation.emptyCredentials);
      }
    },
    validationSchema: formikValidationSchema,
    validateOnChange: false,
    validateOnBlur: false
  });

  return (
    <>
      <div className='main-container'>
        <Header {...props} />
        <div className='loginpage-inner-container'>
          <Container>
            <h3>{text.heading}</h3>
            <Row>
              <Col md={8}>
                <p className='text2r loginpage-description'>
                  {text.description}
                </p>
                <form
                  onSubmit={(event) => {
                    setLoginFailedMessageState(null);
                    formik.handleSubmit(event);
                  }}>
                  {loginFailedMessageState && (
                    <Row>
                      <Col>
                        <AlertBox
                          type='danger'
                          text={loginFailedMessageState}
                          icon={<AiOutlineExclamationCircle />}
                          iconConfig={{ size: '18px' }}
                        />
                      </Col>
                    </Row>
                  )}
                  <Row>
                    <Col className='loginpage-field-container'>
                      <label htmlFor='email' className='text3m'>
                        {text.form_label_1}
                      </label>
                      <input
                        id='email'
                        name='email'
                        type='email'
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        placeholder={text.placeholder}
                        className='loginpage-field-input loginpage-description'
                      />
                      <label className='loginpage-validation-error-label'>
                        {formik.errors.email}
                      </label>
                    </Col>
                  </Row>
                  <Row>
                    <Col className='loginpage-field-container'>
                      <label htmlFor='password' className='text3m'>
                        {text.form_label_2}
                      </label>
                      <div className='form-item'>
                        <input
                          id='password'
                          name='password'
                          type={!showPassword ? 'password' : 'text'}
                          value={formik.values.password}
                          onChange={formik.handleChange}
                          placeholder={text.placeholder}
                          className='loginpage-field-input loginpage-description'
                        />
                        {showPassword && (
                          <FontAwesomeIcon
                            icon={faEyeSlash}
                            size={'lg'}
                            onClick={() => setShowPassword(!showPassword)}
                          />
                        )}
                        {!showPassword && (
                          <FontAwesomeIcon
                            icon={faEye}
                            size={'lg'}
                            onClick={() => setShowPassword(!showPassword)}
                          />
                        )}
                      </div>
                      <label className='loginpage-validation-error-label'>
                        {formik.errors.password}
                      </label>
                    </Col>
                  </Row>
                  <div className='loginpage-button-container'>
                    <Button
                      type='submit'
                      disabled={sessionData.requesting}
                      className={'btn btn-pink mobile-loginpage-button-width'}>
                      {sessionData.requesting ? (
                        <Spinner
                          animation='border'
                          role='status'
                          color='#FFFFFF'
                        />
                      ) : (
                        text.button.label
                      )}
                    </Button>
                  </div>
                </form>
              </Col>
              <Col
                md={{ span: 3, offset: 1 }}
                xs={0}
                className='mobile-loginpage-image'>
                <img
                  src='images/image-registration-page.png'
                  className='loginpage-image-1'
                  alt='registration image'
                />
              </Col>
            </Row>
          </Container>
        </div>

        <Footer {...props} />
      </div>
    </>
  );
}
